import {roundToDecimals} from "@reva/shared/math";
import {formatTypedValue, parseGuiType} from "@reva/shared/number-format";
import type {AvmSpecOutputModelParameter, RevaSpecObjectInputRow, RevaValueParameterType} from "@reva/shared/reva-spec";
import type {RevaSpecOutputRow} from "@reva/shared/spec-conversion";

import type {StandardGroundValueItem} from "../api/payload-types/locationinfo-standard-ground-value";
import type {ValueIndicationResponse} from "../api/payload-types/value-indication";
import {groundValueServiceForWeblink} from "./groundValueServices";
import {camelToSnake} from "./stringUtil";

/** One row of the ground value panel: the spec-described value plus its localized labels. */
export interface StandardGroundValueParameterData {
    /** Key within the ground value item, e.g. "bodenrichtwert". */
    key: string;
    /** Target parameter in REVA; absent when the spec knows no input parameter for this key. */
    parameter?: string;
    parameterType?: RevaValueParameterType;
    titleDe: string;
    titleEn: string;
    value: string | number;
    valueDe: string | number;
    valueEn: string | number;
    descriptionDe?: string;
    descriptionEn?: string;
}

/**
 * Find most fitting Standard ground value (raw).
 * @param standardGroundValueFromInput SGV value (from REVA valuation)
 * @param standardGroundValueValuationDateFromInput SGV valuation date (from REVA valuation)
 * @param indicationData VALUE indication data
 * @param groundValues found ground values
 */
export function findMostFittingGroundValueRaw(
    standardGroundValueFromInput: number | null,
    standardGroundValueValuationDateFromInput: string | null,
    indicationData: ValueIndicationResponse | null,
    groundValues: StandardGroundValueItem[] | null,
): StandardGroundValueItem | null {
    if (!groundValues || groundValues.length === 0) return null;

    const findFittingGroundValue = (bodenrichtwert: number, stichtag: string) =>
        groundValues.find(
            (v) =>
                roundToDecimals(v.bodenrichtwert, 1) === roundToDecimals(bodenrichtwert, 1) && v.stichtag === stichtag,
        ) ?? null;

    if (standardGroundValueFromInput && standardGroundValueValuationDateFromInput)
        return findFittingGroundValue(standardGroundValueFromInput, standardGroundValueValuationDateFromInput);
    if (!indicationData) return null;

    const groundValueFromIndication = Object.values(indicationData)
        .filter(
            (v): v is Record<string, unknown> =>
                !!v && typeof v === "object" && Object.hasOwn(v, "standard_ground_value"),
        )
        .flatMap((v) => Object.entries(v).filter(([k]) => k.startsWith("standard_ground_value")))
        .reduce<Record<string, unknown>>((acc, [k, v]) => {
            acc[k] = v;
            return acc;
        }, {});
    if (groundValueFromIndication.standard_ground_value_precision !== 3) {
        return null;
    }
    const value = groundValueFromIndication.standard_ground_value;
    const valuationDate = groundValueFromIndication.standard_ground_value_valuation_date;
    if (typeof value !== "number" || typeof valuationDate !== "string") return null;
    return findFittingGroundValue(value, valuationDate);
}

/**
 * Find most fitting Standard ground value.
 * @param groundValueFromLocationInfo the ground value item to describe
 * @param locInfoSpec model properties of the locationInfo ground value list
 * @param inputSpec REVA input rows, keyed by parameter
 * @param outputSpec REVA output rows, keyed by the parameter of their factor cell
 */
export function findMostFittingGroundValue(
    groundValueFromLocationInfo: StandardGroundValueItem | null,
    locInfoSpec: Record<string, AvmSpecOutputModelParameter>,
    inputSpec: Record<string, RevaSpecObjectInputRow>,
    outputSpec: Record<string, RevaSpecOutputRow> | null,
): Record<string, StandardGroundValueParameterData> | null {
    if (!groundValueFromLocationInfo) return null;
    const specParams = inputSpec;
    const service = groundValueServiceForWeblink(groundValueFromLocationInfo.weblink);
    const serviceTitle = service?.title ?? new URL(groundValueFromLocationInfo.weblink).host;
    const serviceLicense = service?.license ?? "";
    const vonwannYear = new Date(groundValueFromLocationInfo.vonwann).getFullYear();
    const result: Record<string, StandardGroundValueParameterData> = {};
    for (const key in groundValueFromLocationInfo) {
        const specEntry = locInfoSpec[key];
        if (!specEntry) {
            continue;
        }
        const value = groundValueFromLocationInfo[key as keyof StandardGroundValueItem];
        if (key === "bodenrichtwert") {
            const parameter = "standard_ground_value";
            const cell = outputSpec?.[parameter]?.factor;
            const guiType = parseGuiType(cell?.format, cell?.unitDe, cell?.unitEn, cell?.categories ?? undefined);
            const valueDe = formatTypedValue("de", value, guiType);
            const valueEn = formatTypedValue("en", value, guiType);
            result[key] = {
                key,
                titleDe: specEntry.nameDe,
                titleEn: specEntry.nameEn,
                parameter,
                parameterType: "PLOT",
                value,
                valueDe,
                valueEn,
                descriptionDe: specEntry.descriptionDe,
                descriptionEn: specEntry.descriptionEn,
            };
            continue;
        }
        const paramDef = specParams[`standard_ground_value_${camelToSnake(key)}`];
        const parameter = paramDef?.parameter;
        const parameterType = paramDef?.parameterType;
        if (specEntry.type !== "category") {
            result[key] = {
                key,
                titleDe: specEntry.nameDe,
                titleEn: specEntry.nameEn,
                parameter,
                parameterType,
                value,
                valueDe: value,
                valueEn: value,
                descriptionDe: specEntry.descriptionDe,
                descriptionEn: specEntry.descriptionEn,
            };
            continue;
        }
        const category = specEntry.categories?.find((c) => c.categoryNumber === value);
        if (!category) {
            console.warn(`No category found for ${key}=${value}`);
            continue;
        }
        result[key] = {
            key,
            titleDe: specEntry.nameDe,
            titleEn: specEntry.nameEn,
            parameter,
            parameterType,
            value,
            valueDe: category.titleDe,
            valueEn: category.titleEn,
            descriptionDe: specEntry.descriptionDe,
            descriptionEn: specEntry.descriptionEn,
        };
    }
    const source = !groundValueFromLocationInfo.bezGutachterauschusses
        ? `${serviceTitle} ${serviceLicense} ${vonwannYear}`
        : `${serviceTitle} © ${groundValueFromLocationInfo.bezGutachterauschusses} ${vonwannYear} ${serviceLicense}`;
    result.standard_ground_value_source = {
        key: "standard_ground_value_source",
        parameter: "standard_ground_value_source",
        parameterType: "DOC_PROPERTY",
        titleDe: "Quelle",
        titleEn: "Source",
        value: source,
        valueDe: source,
        valueEn: source,
        descriptionDe: "Quelle der Daten",
        descriptionEn: "Source of the data",
    };
    result.standard_ground_value_number = {
        ...result.wnum,
        key: "standard_ground_value_number",
        parameter: "standard_ground_value_number",
        parameterType: "DOC_PROPERTY",
    };
    result.standard_ground_value_valuation_date = {
        ...result.stichtag,
        key: "standard_ground_value_valuation_date",
        parameter: "standard_ground_value_valuation_date",
        parameterType: "PLOT",
    };
    return result;
}
